<template>
    <div :id="component_id" class="messenger-avatar inline-flex items-center justify-center overflow-hidden" :class="{'messenger-avatar--glyph': monochromeIcon}" :style="avatar_style" role="img">
    <img v-if="use_image || iconSrc" :src="use_image ? imageSrc : iconSrc" :alt="alt_text" class="messenger-avatar-img w-full h-full" :class="{'messenger-avatar-img--glyph': monochromeIcon}" :style="img_style" @error="onImgError" />
    <span v-else-if="icon" class="messenger-avatar-emoji" :style="emoji_style">{{ icon }}</span>
    <span v-else class="messenger-avatar-initials">{{ initials }}</span>
  </div>
</template>

<style scoped>
  .messenger-avatar {
    user-select: none;
    border: 1px solid rgba(0,0,0,0.05);
  }

  /* Single-color glyph avatars (the Rey spark) opt in via the monochromeIcon prop. The hairline ring
     above is hardcoded black-at-5%, which vanishes against a dark page. --border-separator is the
     lightest-weight border token, so the ring reads as a soft edge that sits back into the page in
     both themes rather than drawing attention to itself. */
  .messenger-avatar--glyph {
    border-color: var(--border-separator);
  }
  
  .messenger-avatar-img {
    object-fit: cover;
    display: block;
    transform-origin: center center;
  }

  /* Glyph SVGs are authored with fill="currentColor", but they are handed to <img src>, where
     currentColor cannot inherit the page color and falls back to black — unreadable in dark mode.
     This is the same treatment .rr-logo uses in FusionToolbar.vue, so both glyphs land on the
     identical white. Never applied to photos: it would flatten them to white silhouettes. */
  [data-theme="dark"] .messenger-avatar-img--glyph {
    filter: brightness(0) invert(1);
  }
  
  .messenger-avatar-initials {
    font-weight: 600;
    line-height: 1;
  }

  .messenger-avatar-emoji {
    line-height: 1;
  }
</style>

<script>
export default {
  props: {
    // Path/URL to the avatar image. If provided and loads successfully, it will be used.
    imageSrc: {
      type: String,
      default: ''
    },
    // Person's full name. Used to generate initials if no image.
    name: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    iconSrc: {
      type: String,
      default: ''
    },
    // Marks the supplied imageSrc/iconSrc as a single-color glyph rather than a photo, which lets the
    // component tint it per theme. Leave false for user photos and multi-color logos.
    monochromeIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    // Text color when rendering initials. Ignored when image is shown.
    color: {
      type: String,
      default: ''
    },
    bgColor: {
      type: String,
      default: ''
    },
    // Diameter/size of the avatar in pixels.
    size: {
      type: [Number, String],
      default: 40
    },
    // If true, will add a full border-radius to make it a circle.
    rounded: {
      type: Boolean,
      default: true
    },
    // Optional explicit font size (in px). If not provided, it will be derived from size.
    fontSize: {
      type: [Number, String],
      default: null
    },
    // Optional fallback text for accessibility when using an image.
    alt: {
      type: String,
      default: ''
    },
    // How much to zoom the image to avoid letterboxing within the circular mask.
    // 1 means no zoom; 1.1 means 10% zoom in.
    imageZoom: {
      type: Number,
      default: 1.5
    }
  },
  data() {
    return {
      img_failed: false,
      default_text_color: '#FFFFFF',
      default_bg_color: '#DDE0E9',
    }
  },
  computed: {
    component_key() {
      return this.$attrs.key?this.$attrs.key:('messenger_avatar_'+Date.now());
    },
    component_id() {
      return this.component_key+'_container';
    },
    use_image() {
      return !!this.imageSrc && !this.img_failed;
    },
    initials() {
      if (!this.name || !this.name.trim()) return '';
      const cleanName = this.name.replace(/[^a-zA-Z0-9\s+]/g, '');
      const parts = cleanName.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      // Take first letter of first and last non-empty segments
      const first = parts[0].charAt(0).toUpperCase();
      const last = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${first}${last}`;
    },
    text_color() {
      return this.color && !this.use_image ? this.color : this.default_text_color;
    },
    bg_color() {
      return this.bgColor && !this.use_image ? this.bgColor : this.default_bg_color;
    },
    numeric_size() {
      const s = this.size;
      const n = typeof s === 'string' ? parseInt(s, 10) : s;
      return Number.isFinite(n) && n > 0 ? n : 40;
    },
    computed_font_size() {
      if (this.fontSize) {
        const f = typeof this.fontSize === 'string' ? parseInt(this.fontSize, 10) : this.fontSize;
        return Number.isFinite(f) && f > 0 ? `${f}px` : `${Math.round(this.numeric_size * 0.45)}px`;
      }
      // Rough heuristic for initials size relative to avatar
      return `${Math.round(this.numeric_size * 0.45)}px`;
    },
    avatar_style() {
      return {
        width: this.numeric_size + 'px',
        height: this.numeric_size + 'px',
        background: this.use_image ? 'transparent' : this.bg_color,
        color: this.text_color,
        fontSize: this.computed_font_size,
        borderRadius: this.rounded ? '9999px' : '6px'
      };
    },
    img_style() {
      const z = Number.isFinite(this.imageZoom) && this.imageZoom > 0 ? this.imageZoom : 1;
      return {
        transform: `scale(${z})`
      };
    },
    emoji_style() {
      return { fontSize: `${Math.round(this.numeric_size * 0.6)}px` };
    },
    alt_text() {
      if (this.alt) return this.alt;
      if (this.name) return `Avatar for ${this.name}`;
      return this.$t('messaging.avatarAlt');
    },
  },
  methods: {
    onImgError() {
      this.img_failed = true;
    }
  }
}
</script>

